"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseViewQueryState = void 0;
const ramda_1 = require("ramda");
const view_query_params_models_1 = require("./view-query-params.models");
const table_config_1 = require("../../store/shared/table-config");
const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isString = (value) => typeof value === 'string';
const isBoolean = (value) => typeof value === 'boolean';
const isStringArray = (value) => Array.isArray(value) && value.every(isString);
const isValidNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const assignOptional = (source, target, key, validator, transform = (value) => value) => {
    if (!(key in source))
        return true;
    if (!validator(source[key]))
        return false;
    target[key] = transform(source[key]);
    return true;
};
const assignOptionalFields = (source, target, keys, validator) => keys.every((key) => assignOptional(source, target, key, validator));
const parseDateValue = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime()))
        return value;
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
    return undefined;
};
const assignOptionalDate = (source, target, key) => {
    if (!(key in source))
        return true;
    const date = parseDateValue(source[key]);
    if (!date)
        return false;
    target[key] = date;
    return true;
};
const parseFilterWithStringMetadata = (value, type, keys = []) => {
    const filter = { type };
    const fields = [...keys, view_query_params_models_1.TableColumnFilterField.Operator, view_query_params_models_1.TableColumnFilterField.Label];
    return assignOptionalFields(value, filter, fields, isString) ? filter : null;
};
const parseMultiSelectFilter = (value) => {
    const filter = parseFilterWithStringMetadata(value, table_config_1.TableColumnFilterTypes.MultiSelect);
    if (!filter)
        return null;
    const isValid = assignOptional(value, filter, view_query_params_models_1.TableColumnFilterField.List, isStringArray)
        && assignOptional(value, filter, view_query_params_models_1.TableColumnFilterField.ExcludedValues, isStringArray)
        && assignOptional(value, filter, view_query_params_models_1.TableColumnFilterField.SelectAll, isBoolean);
    return isValid ? filter : null;
};
const parseDateFilter = (value) => {
    const filter = parseFilterWithStringMetadata(value, table_config_1.TableColumnFilterTypes.Date);
    if (!filter)
        return null;
    const isValid = assignOptionalDate(value, filter, view_query_params_models_1.TableColumnFilterField.StartAt)
        && assignOptionalDate(value, filter, view_query_params_models_1.TableColumnFilterField.EndAt);
    return isValid && (filter.startAt || filter.endAt) ? filter : null;
};
const parseNumberFilter = (value) => {
    const filter = parseFilterWithStringMetadata(value, table_config_1.TableColumnFilterTypes.Number);
    if (!filter)
        return null;
    const isValid = assignOptional(value, filter, view_query_params_models_1.TableColumnFilterField.Low, isValidNumber)
        && assignOptional(value, filter, view_query_params_models_1.TableColumnFilterField.High, isValidNumber);
    return isValid && (view_query_params_models_1.TableColumnFilterField.Low in filter || view_query_params_models_1.TableColumnFilterField.High in filter) ? filter : null;
};
const parseColumnFilter = (value) => {
    if (!isRecord(value))
        return null;
    switch (value[view_query_params_models_1.TableColumnFilterField.Type]) {
        case table_config_1.TableColumnFilterTypes.Text:
            return parseFilterWithStringMetadata(value, table_config_1.TableColumnFilterTypes.Text, [view_query_params_models_1.TableColumnFilterField.Search, view_query_params_models_1.TableColumnFilterField.Match]);
        case table_config_1.TableColumnFilterTypes.MultiSelect:
            return parseMultiSelectFilter(value);
        case table_config_1.TableColumnFilterTypes.Date:
            return parseDateFilter(value);
        case table_config_1.TableColumnFilterTypes.Number:
            return parseNumberFilter(value);
        default:
            return null;
    }
};
const parseTableFilterItem = (value) => {
    if (!isRecord(value) || typeof value[view_query_params_models_1.TableFilterItemField.Field] !== 'string')
        return null;
    const filter = parseColumnFilter(value[view_query_params_models_1.TableFilterItemField.Filter]);
    return filter ? { field: value[view_query_params_models_1.TableFilterItemField.Field], filter } : null;
};
const parseTableFilter = (value) => {
    if (!isRecord(value) || !Array.isArray(value[view_query_params_models_1.TableFilterField.Items]))
        return null;
    const logicOperator = value[view_query_params_models_1.TableFilterField.LogicOperator] === 'or' || value[view_query_params_models_1.TableFilterField.LogicOperator] === 'and'
        ? value[view_query_params_models_1.TableFilterField.LogicOperator]
        : null;
    if (!logicOperator)
        return null;
    const items = value[view_query_params_models_1.TableFilterField.Items].map(parseTableFilterItem);
    if (!items.every((item) => item != null))
        return null;
    return { items, logicOperator };
};
const parseTableSort = (value) => {
    if (value == null)
        return null;
    if (!isRecord(value) || typeof value[view_query_params_models_1.TableSortField.Key] !== 'string')
        return undefined;
    if (value[view_query_params_models_1.TableSortField.Direction] !== 0 /* SortDirection.Ascending */
        && value[view_query_params_models_1.TableSortField.Direction] !== 1 /* SortDirection.Descending */)
        return undefined;
    const sort = {
        key: value[view_query_params_models_1.TableSortField.Key],
        direction: value[view_query_params_models_1.TableSortField.Direction],
    };
    return assignOptional(value, sort, view_query_params_models_1.TableSortField.Default, isBoolean)
        && assignOptional(value, sort, view_query_params_models_1.TableSortField.IsAIEnriched, isBoolean)
        ? sort
        : undefined;
};
const parseReviewAttribute = (value) => {
    if (!isRecord(value) || typeof value[view_query_params_models_1.ReviewAttributeField.Id] !== 'string')
        return null;
    const reviewAttribute = { id: value[view_query_params_models_1.ReviewAttributeField.Id] };
    return assignOptional(value, reviewAttribute, view_query_params_models_1.ReviewAttributeField.PreviousIndex, isValidNumber) ? reviewAttribute : null;
};
const parsePinnedColumns = (value) => {
    if (!isRecord(value))
        return null;
    const pinnedColumns = {};
    const isValid = assignOptional(value, pinnedColumns, 'left', isStringArray)
        && assignOptional(value, pinnedColumns, 'right', isStringArray);
    return isValid ? pinnedColumns : null;
};
const parseDynamicColumns = (value) => {
    if (!isRecord(value))
        return null;
    const dynamicColumns = {};
    const isValid = assignOptional(value, dynamicColumns, view_query_params_models_1.TableDynamicColumnsField.ColumnsOrder, isStringArray)
        && assignOptional(value, dynamicColumns, view_query_params_models_1.TableDynamicColumnsField.ExcludedColumns, isStringArray)
        && assignOptional(value, dynamicColumns, view_query_params_models_1.TableDynamicColumnsField.FilterOptions, isStringArray)
        && assignOptional(value, dynamicColumns, view_query_params_models_1.TableDynamicColumnsField.SelectAllMode, isBoolean);
    if (!isValid)
        return null;
    if (view_query_params_models_1.TableDynamicColumnsField.PinnedColumns in value) {
        const pinnedColumns = parsePinnedColumns(value[view_query_params_models_1.TableDynamicColumnsField.PinnedColumns]);
        if (!pinnedColumns)
            return null;
        dynamicColumns.pinnedColumns = pinnedColumns;
    }
    if (view_query_params_models_1.TableDynamicColumnsField.ReviewAttribute in value) {
        const reviewAttribute = parseReviewAttribute(value[view_query_params_models_1.TableDynamicColumnsField.ReviewAttribute]);
        if (!reviewAttribute)
            return null;
        dynamicColumns.reviewAttribute = reviewAttribute;
    }
    return (0, ramda_1.isEmpty)(dynamicColumns) ? null : dynamicColumns;
};
const parseMiddleSectionFilter = (value) => {
    if (value == null)
        return null;
    if (!isRecord(value) || typeof value[view_query_params_models_1.MiddleSectionFilterField.Label] !== 'string')
        return undefined;
    const middleSectionFilter = {
        label: value[view_query_params_models_1.MiddleSectionFilterField.Label],
    };
    const isValid = assignOptional(value, middleSectionFilter, view_query_params_models_1.MiddleSectionFilterField.Value, (fieldValue) => typeof fieldValue === 'string' || typeof fieldValue === 'number')
        && assignOptional(value, middleSectionFilter, view_query_params_models_1.MiddleSectionFilterField.Unit, isString);
    return isValid ? middleSectionFilter : undefined;
};
const parseString = (value) => (typeof value === 'string' ? value : undefined);
const parsePageSize = (value) => (isValidNumber(value) && value > 0 ? value : undefined);
const viewQueryStateFieldParsers = {
    [view_query_params_models_1.ViewQueryStateField.ProjectId]: {
        parser: parseString,
        shouldAssign: Boolean,
    },
    [view_query_params_models_1.ViewQueryStateField.CatalogId]: {
        parser: parseString,
        shouldAssign: Boolean,
    },
    [view_query_params_models_1.ViewQueryStateField.Filter]: {
        parser: parseTableFilter,
        shouldAssign: (0, ramda_1.complement)(ramda_1.isNil),
    },
    [view_query_params_models_1.ViewQueryStateField.Search]: {
        parser: parseString,
        shouldAssign: Boolean,
    },
    [view_query_params_models_1.ViewQueryStateField.Sort]: {
        parser: parseTableSort,
        shouldAssign: (value) => value !== undefined,
    },
    [view_query_params_models_1.ViewQueryStateField.PageSize]: {
        parser: parsePageSize,
        shouldAssign: Boolean,
    },
    [view_query_params_models_1.ViewQueryStateField.DynamicColumns]: {
        parser: parseDynamicColumns,
        shouldAssign: (0, ramda_1.complement)(ramda_1.isNil),
    },
    [view_query_params_models_1.ViewQueryStateField.MiddleSectionFilter]: {
        parser: parseMiddleSectionFilter,
        shouldAssign: (value) => value !== undefined,
    },
    [view_query_params_models_1.ViewQueryStateField.SelectedTreeNodePath]: {
        parser: parseString,
        shouldAssign: Boolean,
    },
};
const parseViewQueryStateFields = (value, state) => {
    Object.entries(viewQueryStateFieldParsers).forEach(([field, { parser, shouldAssign }]) => {
        if (!(field in value))
            return;
        const parsedValue = parser(value[field]);
        if (shouldAssign(parsedValue)) {
            state[field] = parsedValue;
        }
    });
};
const parseViewQueryState = (value) => {
    if (!isRecord(value) || value[view_query_params_models_1.ViewQueryStateField.Version] !== view_query_params_models_1.VIEW_QUERY_STATE_VERSION)
        return null;
    const state = { [view_query_params_models_1.ViewQueryStateField.Version]: view_query_params_models_1.VIEW_QUERY_STATE_VERSION };
    parseViewQueryStateFields(value, state);
    return state;
};
exports.parseViewQueryState = parseViewQueryState;
